import { beginAvailabilityRequest, isProfileAvailabilityConflict } from '~/utils/applicationAvailability'
import Vue from 'vue'
import { deleteCookie } from '~/plugins/utm-interceptor'
import { ANALYTICS_TRIGGERS } from '~/utils/analyticsTriggers'
import { isExpectedAuthenticationFailure } from '~/utils/authenticationFailure'

function translateWithFallback(i18n, key, params, fallback) {
	const translated = i18n.t(key, params)

	return translated && translated !== key ? translated : fallback
}

function affectedApplicants(i18n, count, status, sendRejectionEmail) {
	if (
		status === 'denied' &&
		count > 0 &&
		typeof sendRejectionEmail === 'boolean'
	) {
		const single = count === 1
		const key = sendRejectionEmail
			? single
				? 'email-sent'
				: 'batch-emails-sent'
			: single
			? 'no-email-sent'
			: 'batch-no-emails-sent'
		const declined = single
			? 'Candidate declined.'
			: `${count} candidates declined.`
		const email = sendRejectionEmail
			? single
				? 'Email sent.'
				: 'Emails sent.'
			: single
			? 'No email sent.'
			: 'No emails sent.'
		return translateWithFallback(
			i18n,
			`application.decline.toast.${key}`,
			{ count },
			`${declined} ${email}`
		)
	}
	if (status === 'accepted') {
		return translateWithFallback(
			i18n,
			count === 1
				? 'application.shortlist.toast.single'
				: 'application.shortlist.toast.multiple',
			{ count },
			count === 1
				? 'Candidate shortlisted'
				: `${count} candidates shortlisted`
		)
	}
	if (status === 'hired') {
		return translateWithFallback(
			i18n,
			count === 1
				? 'application.hired.toast.single'
				: 'application.hired.toast.multiple',
			{ count },
			count === 1 ? 'Candidate hired' : `${count} candidates hired`
		)
	}
	return translateWithFallback(
		i18n,
		'application.action.affected',
		{ count },
		`Candidates affected: ${count}.`
	)
}

function interviewToast(i18n, result) {
	if (result.moved_count !== 1 || ![0, 1].includes(result.email_count)) {
		return affectedApplicants(i18n, result.moved_count)
	}
	const sent = result.email_count === 1
	return translateWithFallback(
		i18n,
		sent
			? 'application.interview-invitation.toast.single-sent'
			: 'application.interview-invitation.toast.single-without',
		{},
		sent
			? 'Invitation sent. Candidate moved to Interview'
			: 'Candidate moved to Interview. No invitation sent'
	)
}

function getWithdrawalToast(application, i18n) {
	const fallbackJobTitle = i18n.t('general.job-offer') || 'job'
	const jobTitle = application?.job_offer?.name || fallbackJobTitle

	return {
		title: translateWithFallback(
			i18n,
			'application.withdraw.toast.title',
			{},
			'Application withdrawn'
		),
		message: translateWithFallback(
			i18n,
			'application.withdraw.toast.message',
			{ job: jobTitle },
			`Your application for the ${jobTitle} role has been withdrawn.`
		)
	}
}

function preserveViewedState(state, application) {
	return state.viewedApplicationIds?.includes(Number(application.id))
		? { ...application, is_new_for_current_user: false }
		: application
}

export const getDefaultState = () => ({
	sessionGeneration: 0,
	viewedApplicationIds: [],
	applications: [],
	availabilityByProfileId: {},
	availabilityRequestIdByProfileId: {},
	latestAvailabilityRequestId: 0,
	pagination: {},
	statusCounts: {},
	statusHasStrongProfiles: {},
	latestFetchRequestId: 0,
	listRefreshRequest: null,
	orderBy: 'match'
})

export const state = () => getDefaultState()

function preserveReconciledAvailability(state, application, request) {
	const profileId = application?.applicant_profile?.id || application?.applicant_profile_id
	const availability = state.availabilityByProfileId?.[profileId]
	// Compare request start order, not response arrival or object identity.
	if (!availability || (state.availabilityRequestIdByProfileId?.[profileId] || 0) <= request.id) {
		return application
	}
	return {
		...application,
		applicant_profile: { ...application.applicant_profile, ...availability }
	}
}

function reconcileProfileAvailability(state, application, request) {
	const profile = application?.applicant_profile
	const profileId = profile?.id || application?.applicant_profile_id
	if (!profileId || !profile || !('is_deactivated' in profile)) return application
	request = request || beginAvailabilityRequest(state, (_, id) => { state.latestAvailabilityRequestId = id })
	if (request.sessionGeneration !== state.sessionGeneration) return application
	const current = preserveReconciledAvailability(state, application, request)
	if (current !== application) return current
	const availability = {
		is_deactivated: profile.is_deactivated,
		deactivated_at: profile.deactivated_at
	}
	if (!state.availabilityByProfileId) Vue.set(state, 'availabilityByProfileId', {})
	if (!state.availabilityRequestIdByProfileId) Vue.set(state, 'availabilityRequestIdByProfileId', {})
	Vue.set(state.availabilityByProfileId, profileId, availability)
	Vue.set(state.availabilityRequestIdByProfileId, profileId, request.id)
	state.applications.forEach(current => {
		if ((current.applicant_profile?.id || current.applicant_profile_id) == profileId) {
			Vue.set(current, 'applicant_profile', { ...current.applicant_profile, ...availability })
		}
	})
	return application
}

export const actions = {
	async reconcileAvailabilityConflict({ commit, dispatch, state }, { error, applicationIds }) {
		if (!isProfileAvailabilityConflict(error)) return false
		const generation = state.sessionGeneration
		const results = await Promise.allSettled([...new Set(applicationIds)].map(async id => {
			const availabilityRequest = beginAvailabilityRequest(state, commit)
			const response = await this.$axios.get(`/application/${id}`, { __skipGlobalErrorToast: true })
			if (state.sessionGeneration === generation) {
				commit('reconcileProfileAvailability', { data: response.data, availabilityRequest })
			}
		}))
		results.forEach(result => {
			if (result.status === 'rejected') console.warn('Could not refresh applicant availability:', result.reason)
		})
		if (state.sessionGeneration === generation) {
			dispatch('toast/push', {
				title: this.app.i18n.t('application.profile-deactivated.badge'),
				message: this.app.i18n.t('application.profile-deactivated.explanation'),
				type: 'error'
			}, { root: true })
		}
		return true
	},
	async recordView({ commit, dispatch, state }, id) {
		const sessionGeneration = state.sessionGeneration
		await this.$axios.$post(`/application/${id}/view`)
		if (sessionGeneration !== state.sessionGeneration) return false

		commit('markViewed', id)
		const results = await Promise.allSettled([
			dispatch('notification/fetch', {}, { root: true }),
			dispatch('notification/fetchCount', null, { root: true })
		])
		results.forEach(result => {
			if (result.status === 'rejected') {
				console.warn(
					'Failed to refresh notifications after viewing application:',
					result.reason
				)
			}
		})
		return sessionGeneration === state.sessionGeneration
	},

	async fetch(
		{ commit, state = {} },
		{ skipGlobalErrorToast = false, ...params } = { page: 1 }
	) {
		const requestId = Number(state.latestFetchRequestId || 0) + 1
		commit('setLatestFetchRequestId', requestId)
		const availabilityRequest = beginAvailabilityRequest(state, commit)

		const queryString = Object.entries(params)
			.map(
				([key, value]) =>
					`${encodeURIComponent(key)}=${encodeURIComponent(value)}`
			)
			.join('&')

		const response = await this.$axios.$get(`/application?${queryString}`, {
			__skipGlobalErrorToast: skipGlobalErrorToast
		})

		const data = response.data.map(application =>
			preserveReconciledAvailability(state, application, availabilityRequest)
		)

		if (requestId === state.latestFetchRequestId) {
			commit('set', { data, meta: response.meta, availabilityRequest })
		}

		return { ...response, data }
	},

	async needApplications({ dispatch, state }) {
		if (state.applications.length <= 0) {
			await dispatch('fetch')
		}

		return state.applications
	},

	async update({ dispatch, commit, state }, data) {
		if (data.rejectionResult) {
			const applications = await dispatch('applyRejectionResult', data.rejectionResult)
			return applications.find(application => application.id == data.id)
		}
		const availabilityRequest = beginAvailabilityRequest(state, commit)
		const application = state.applications.find(app => app.id == data.id)
		const { sendRejectionEmail, withdrawal, ...requestData } = data

		if (typeof sendRejectionEmail === 'boolean') {
			requestData.send_rejection_email = sendRejectionEmail
		}

		return this.$axios
			.patch(`/application/${data.id}`, requestData, {
				__skipGlobalErrorToast: true
			})
			.then(res => {
				if (availabilityRequest.sessionGeneration !== state.sessionGeneration) return res.data
				const updatedApplication = preserveReconciledAvailability(state, res.data, availabilityRequest)
				commit('update', { data: updatedApplication, availabilityRequest })

				// Get context-aware notification message
				const notificationMessage = withdrawal
					? getWithdrawalToast(application, this.app.i18n)
					: {
							title: affectedApplicants(
								this.app.i18n,
								1,
								updatedApplication.status,
								sendRejectionEmail
							)
					  }

				dispatch(
					'toast/push',
					{
						title:
							notificationMessage.title ||
							notificationMessage.message,
						...(notificationMessage.message &&
						notificationMessage.title
							? { message: notificationMessage.message }
							: {}),
						type: 'success'
					},
					{ root: true }
				)

				return updatedApplication
			})
			.catch(async error => {
				if (availabilityRequest.sessionGeneration !== state.sessionGeneration) throw error
				if (await dispatch('reconcileAvailabilityConflict', { error, applicationIds: [data.id] })) throw error
				console.error('Error updating application:', error.response)

				// APR-894: Handle 403 errors specifically - may indicate stale user context
				if (error.response?.status === 403) {
					// Refresh user context to clear any stale organization/role data
					try {
						await dispatch('user/getUser', null, { root: true })
					} catch (refreshError) {
						if (isExpectedAuthenticationFailure(refreshError)) {
							throw refreshError
						}

						console.error(
							'Failed to refresh user after a denied application update:',
							refreshError
						)
					}

					// Show user-friendly error message
					const errorMessage =
						this.app.i18n.t('errors.action-not-allowed') ||
						'You do not have permission to perform this action'

					dispatch(
						'toast/push',
						{
							title:
								this.app.i18n.t('errors.permission-denied') ||
								'Permission Denied',
							message: errorMessage,
							type: 'error'
						},
						{ root: true }
					)
				} else {
					const errorMessage = this.app.i18n.t(
						'general.unexpected-error'
					)

					dispatch(
						'toast/push',
						{
							title: this.app.i18n.t('general.error'),
							message: errorMessage,
							type: 'error'
						},
						{ root: true }
					)
				}

				throw error
			})
	},

	async interviewTemplates(_, payload) {
		return this.$axios.$post('/application/interview/templates', payload, {
			__skipGlobalErrorToast: true
		})
	},

	async previewInterviewInvitation(_, payload) {
		return this.$axios.$post('/application/interview/preview', payload, {
			__skipGlobalErrorToast: true
		})
	},

	async sendInterviewInvitation({ dispatch, commit, state }, payload) {
		const availabilityRequest = beginAvailabilityRequest(state, commit)
		try {
			const result = await this.$axios.$post(
				'/application/interview',
				payload,
				{ __skipGlobalErrorToast: true }
			)
			return {
				...result,
				availabilityRequest,
				sessionGeneration: availabilityRequest.sessionGeneration
			}
		} catch (error) {
			if (availabilityRequest.sessionGeneration === state.sessionGeneration) {
				await dispatch('reconcileAvailabilityConflict', {
					error,
					applicationIds: payload.application_ids
				})
			}
			throw error
		}
	},

	async moveToInterview(
		{ dispatch, commit, state },
		{ interviewResult: result, refreshList = false }
	) {
		if (
			!result ||
			(result.sessionGeneration !== undefined &&
				result.sessionGeneration !== state.sessionGeneration)
		) return []
		const availabilityRequest =
			result.availabilityRequest || beginAvailabilityRequest(state, commit)
		const applications = (result.applications || []).map(application =>
			preserveReconciledAvailability(state, application, availabilityRequest)
		)
		applications.forEach(application =>
			commit('update', { data: application, availabilityRequest })
		)
		if (refreshList && applications.length) commit('requestListRefresh')
		await dispatch(
			'toast/push',
			{
				title: interviewToast(this.app.i18n, result),
				type: result.failed_count ? 'error' : 'success'
			},
			{ root: true }
		)
		return applications
	},

	async applyRejectionResult({ dispatch, commit, state }, result) {
		if (
			result.sessionGeneration !== undefined &&
			result.sessionGeneration !== state.sessionGeneration
		) return []
		const availabilityRequest = result.availabilityRequest || beginAvailabilityRequest(state, commit)
		const applications = (result.applications || []).map(application =>
			preserveReconciledAvailability(state, application, availabilityRequest)
		)
		applications.forEach(application =>
			commit('update', { data: application, availabilityRequest })
		)
		if (result.refreshList && applications.length) commit('requestListRefresh')
		await dispatch(
			'toast/push',
			{
				title: affectedApplicants(
					this.app.i18n,
					result.declined_count,
					'denied',
					result.email_count === result.declined_count
						? true
						: result.email_count === 0
						? false
						: undefined
				),
				type: result.failed_count ? 'error' : 'success'
			},
			{ root: true }
		)
		return applications
	},

	async batchUpdate(
		{ dispatch, commit, state },
		{ applicationIds, status, sendRejectionEmail, rejectionResult }
	) {
		if (rejectionResult) return dispatch('applyRejectionResult', rejectionResult)
		const availabilityRequest = beginAvailabilityRequest(state, commit)

		const payload = {
			application_ids: applicationIds,
			status: status
		}

		if (typeof sendRejectionEmail === 'boolean') {
			payload.send_rejection_email = sendRejectionEmail
		}

		return this.$axios
			.patch('/application/batch', payload, {
				__skipGlobalErrorToast: true
			})
			.then(res => {
				if (availabilityRequest.sessionGeneration !== state.sessionGeneration) return res.data
				const updatedApplications = Array.isArray(res.data)
					? res.data.map(application => preserveReconciledAvailability(state, application, availabilityRequest))
					: res.data
				// Update all applications in the store
				if (Array.isArray(updatedApplications)) {
					updatedApplications.forEach(updatedApp => {
						commit('update', { data: updatedApp, availabilityRequest })
					})
				}

				dispatch(
					'toast/push',
					{
						title: affectedApplicants(
							this.app.i18n,
							Array.isArray(updatedApplications)
								? updatedApplications.length
								: 0,
							status,
							sendRejectionEmail
						),
						type: 'success'
					},
					{ root: true }
				)

				return updatedApplications
			})
			.catch(async error => {
				if (availabilityRequest.sessionGeneration !== state.sessionGeneration) throw error
				if (await dispatch('reconcileAvailabilityConflict', { error, applicationIds })) throw error
				console.error('Error in batch update:', error.response)

				const errorMessage =
					this.app.i18n.t('application.batch-update-error') ||
					'Failed to update applications'

				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('general.error'),
						message: errorMessage,
						type: 'error'
					},
					{ root: true }
				)

				throw error
			})
	},

	async submitFeedback(
		{ commit, dispatch },
		{ applicationIds, reasons, workStyleInfluenced, comment, triggerType }
	) {
		return this.$axios
			.post(
				'/application/feedback',
				{
					application_ids: applicationIds,
					reasons,
					work_style_influenced: workStyleInfluenced,
					comment: comment || null,
					trigger_type: triggerType
				},
				{ __skipGlobalErrorToast: true }
			)
			.then(() => {
				// Keep local records in sync with the backend's unique
				// (application_id, user_id) guard so the card never re-prompts
				// (and re-POSTs -> 422) within the session. (APR-1193)
				commit('markFeedbackGiven', applicationIds)
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('feedback.submitted'),
						type: 'success'
					},
					{ root: true }
				)
			})
			.catch(error => {
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('general.error'),
						message: this.app.i18n.t('general.unexpected-error'),
						type: 'error'
					},
					{ root: true }
				)

				throw error
			})
	},

	async delete({ commit }, id) {
		this.$axios.delete(`/application/${id}`).then(() => {
			commit('delete', id)
		})
	},

	store(
		{ dispatch, commit, state, rootState },
		{
			applicantProfileId,
			jobOfferId,
			status = null,
			pathType = null,
			trigger = ANALYTICS_TRIGGERS.APPLICATION_FORM_SUBMIT,
			submissionContext = null,
			sourceSurface = null
		}
	) {
		const sessionGeneration = state.sessionGeneration
		const data = {
			applicant_profile_id: applicantProfileId,
			job_offer_id: jobOfferId,
			status: status
		}

		return this.$axios
			.$post(`/application`, data)
			.then(data => {
				if (sessionGeneration !== state.sessionGeneration) return data
				commit('add', data)

				// Determine if this is an applicant applying or an organization inviting - APR-981
				const currentUser = rootState.user?.user || {}
				const isApplicantContext = submissionContext
					? submissionContext === 'applicant'
					: currentUser.type === 'applicant' ||
					  (!currentUser.current_organization &&
							!!currentUser.applicant_profile)

				// Track application submitted only for applicants, not employer invitations
				const isSubmittedApplication = data.status !== 'denied'

				if (
					process.client &&
					this.app.$analytics &&
					isApplicantContext &&
					isSubmittedApplication
				) {
					const matchScore =
						data.match?.match ??
						data.final_match_score ??
						data.total_match_score ??
						null

					this.app.$analytics.trackApplicationSubmitted({
						applicationId: data.id,
						jobId: data.job_offer_id,
						matchScore,
						pathType:
							pathType ?? this.app.$analytics.detectPathType({}),
						userId: currentUser.id,
						applicantProfileId:
							data.applicant_profile_id ||
							currentUser.applicant_profile?.id,
						trigger,
						userType: 'candidate',
						sourceSurface
					})
				}

				if (isSubmittedApplication) {
					const toastTitleKey = isApplicantContext
						? 'toast.application.sent.title'
						: 'toast.invitation.sent.title'

					dispatch(
						'toast/push',
						{
							title: this.app.i18n.t(toastTitleKey),
							type: 'success'
						},
						{ root: true }
					)
				}

				if (process.client) {
					try {
						deleteCookie('utm_source', this.app.$config)
					} catch (error) {
						console.warn('Could not delete UTM cookie:', error)
					}
				}

				return data
			})
			.catch(async error => {
				if (sessionGeneration !== state.sessionGeneration) throw error
				if (isExpectedAuthenticationFailure(error)) {
					throw error
				}

				const statusCode = error.response?.status || 500
				if (statusCode === 403 && status === 'denied') {
					try {
						const res = await this.$axios.$get(
							`/application?job-offer=${jobOfferId}`
						)
						if (sessionGeneration !== state.sessionGeneration) throw error
						const list = res?.data || []
						const existing = list.find(
							a => a.applicant_profile_id == applicantProfileId
						)
						if (existing && existing.id) {
							await this.dispatch('application/update', {
								id: existing.id,
								status: 'denied'
							})
							return { id: existing.id, status: 'denied' }
						}
					} catch (fallbackError) {
						if (sessionGeneration !== state.sessionGeneration) throw fallbackError
						if (isExpectedAuthenticationFailure(fallbackError)) {
							throw fallbackError
						}
					}
				}

				const errorMessage = this.app.i18n.t('general.unexpected-error')
				throw new Error(errorMessage)
			})
	},

	export(
		{ rootState },
		{ jobOfferId = null, status = null, dateFrom = null, dateTo = null }
	) {
		const params = new URLSearchParams()

		if (jobOfferId) params.append('job_offer_id', jobOfferId)
		if (status) params.append('status', status)
		if (dateFrom) params.append('date_from', dateFrom)
		if (dateTo) params.append('date_to', dateTo)

		const queryString = params.toString()
		const organization = rootState.user.user
			? rootState.user.user.current_organization
			: null

		if (!organization) {
			throw new Error(this.app.i18n.t('errors.no_organization_selected'))
		}

		return this.$axios.get(
			`/organization/${organization.id}/candidates/export${
				queryString ? `?${queryString}` : ''
			}`,
			{
				responseType: 'blob'
			}
		)
	}
}

export const getters = {}

export const mutations = {
	requestListRefresh: state => {
		state.listRefreshRequest = { sessionGeneration: state.sessionGeneration }
	},
	setAvailabilityRequestId: (state, id) => {
		state.latestAvailabilityRequestId = id
	},
	reconcileProfileAvailability: (state, payload) => {
		reconcileProfileAvailability(state, payload.availabilityRequest ? payload.data : payload, payload.availabilityRequest)
	},
	RESET_STATE: state => {
		const nextRequestId = Number(state.latestFetchRequestId || 0) + 1

		Object.assign(state, getDefaultState(), {
			latestFetchRequestId: nextRequestId,
			sessionGeneration: Number(state.sessionGeneration || 0) + 1
		})
	},
	add: (state, payload) => {
		const request = payload.availabilityRequest
		if (request && request.sessionGeneration !== state.sessionGeneration) return
		const data = request ? reconcileProfileAvailability(state, payload.data, request) : payload
		state.applications.push(preserveViewedState(state, data))
	},
	update: (state, payload) => {
		const request = payload.availabilityRequest
		if (request && request.sessionGeneration !== state.sessionGeneration) return
		const data = reconcileProfileAvailability(state, request ? payload.data : payload, request)
		const index = state.applications.findIndex(i => i.id == data.id)
		if (index !== -1) {
			// Detail/status resources omit list-only AI lines. Explicit nulls still
			// clear them, while omitted fields retain the last list response.
			const current = state.applications[index]
			const row = { ...data }
			for (const key of ['short_fit', 'short_risk']) {
				if (!(key in row) && key in current) row[key] = current[key]
			}
			Vue.set(state.applications, index, preserveViewedState(state, row))
		}
	},

	markViewed: (state, id) => {
		id = Number(id)
		if (!state.viewedApplicationIds.includes(id))
			state.viewedApplicationIds.push(id)
		state.applications.forEach(application => {
			if (Number(application.id) === id)
				Vue.set(application, 'is_new_for_current_user', false)
		})
	},

	markFeedbackGiven: (state, applicationIds) => {
		const ids = new Set(applicationIds)
		state.applications.forEach(application => {
			if (ids.has(application.id)) {
				Vue.set(application, 'has_feedback', true)
			}
		})
	},

	delete: (state, id) =>
		Vue.delete(
			state.applications,
			state.applications.indexOf(state.applications.find(i => i.id == id))
		),

	set: (state, { data, meta, availabilityRequest }) => {
		if (availabilityRequest && availabilityRequest.sessionGeneration !== state.sessionGeneration) return
		state.applications = data.map(application =>
			preserveViewedState(state, reconcileProfileAvailability(state, application, availabilityRequest))
		)
		state.pagination = meta
		state.statusCounts = meta?.status_counts || {}
		state.statusHasStrongProfiles = meta?.status_has_strong_profiles || {}
	},
	setLatestFetchRequestId: (state, requestId) => {
		state.latestFetchRequestId = requestId
	},
	sort: (state, newOrder = 'match') => {
		if (state.orderBy != newOrder) {
			state.orderBy = newOrder
		}

		if (state.orderBy == 'match') {
			state.applications = state.applications.sort((a, b) => {
				const matchA = a.match?.match ?? 0
				const matchB = b.match?.match ?? 0
				return matchA < matchB ? 1 : -1
			})
		}

		if (state.orderBy == 'date') {
			state.applications = state.applications.sort((a, b) => {
				return new Date(b.updated_at) - new Date(a.updated_at)
			})
		}
	}
}
